import { Form, FormInstance, message, ModalProps, notification } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { APIClient } from '../../api/useAPIClient';
import useAPIMutation from '../../api/useAPIMutation';
import useCloneJobPostingMutation from '../../hooks/useCloneJobPostingMutation';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { JobPostingCollection, JobPostingDetail } from '../../types';
import CancelVacancyModal from './CancelVacancyModal';
import CloneVacancyModal from './CloneVacancyModal';
import ConcludeVacancyModal from './ConcludeVacancyModal';
import DeleteVacancyModal from './DeleteVacancyModal';
import FreezeVacancyModal from './FreezeVacancyModal';

export type VacancyActions = 'DELETE' | 'SUSPENDED' | 'CANCELED' | 'FINALIZED' | 'REOPEN' | 'CLONE';

interface VacancyActionsProps {
  jobPosting: JobPostingCollection | JobPostingDetail;
  action?: VacancyActions;
  open?: boolean;
  onOk?: () => void;
  onCancel?: () => void;
}

interface MutationsParams {
  jobPosting: JobPostingCollection | JobPostingDetail;
}

export interface JobPostingActionModal extends ModalProps {
  jobPosting: JobPostingCollection | JobPostingDetail;
  form?: FormInstance;
}

const VacancyActionsModal: React.FC<VacancyActionsProps> = ({
  action,
  jobPosting,
  open = false,
  onOk,
  onCancel,
}) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const navigate = useTypedNavigate();
  const { tenant } = useTypedParams({ tenant: String });

  const queryClient = useQueryClient();

  const cloneJobPostingMutation = useCloneJobPostingMutation({
    onSuccess: (data) => {
      navigate({ pathname: '/:tenant/opportunities/:id/change/', params: { tenant, id: data.id } });
      notification.success({
        message: t('Vaga clonada com sucesso!'),
        description: t('Revise todas as configurações da vaga antes de publicar.'),
        duration: 0,
      });
    },
  });

  const handleRequest = async (data: MutationsParams, api: APIClient) => {
    switch (action) {
      case 'FINALIZED':
        return api.post(`job-postings/${data.jobPosting.id}/finalized/`);
      case 'SUSPENDED':
        return api.post(`job-postings/${data.jobPosting.id}/suspended/`, {
          description: form.getFieldValue('comment') || '',
        });
      case 'CANCELED':
        return api.post(`job-postings/${data.jobPosting.id}/canceled/`, {
          description: form.getFieldValue('comment') || '',
        });
      case 'DELETE': {
        return api.delete(`/job-postings/${data.jobPosting.id}/`);
      }
      case 'CLONE':
        return cloneJobPostingMutation.mutateAsync({ jobPostingId: data.jobPosting.id });
    }
  };

  const mutation = useAPIMutation(
    async (data: MutationsParams, api) => await handleRequest(data, api),
    {
      onSuccess: () => {
        switch (action) {
          case 'CANCELED':
            message.success(t('Vaga cancelada com sucesso!'));
            break;
          case 'DELETE':
            message.success(t('Vaga excluída com sucesso!'));
            break;
          case 'FINALIZED':
            message.success(t('Vaga concluída com sucesso!'));
            break;
          case 'SUSPENDED':
            message.success(t('Vaga congelada com sucesso!'));
            break;
        }

        form.resetFields();

        if (onOk) {
          onOk();
        }
      },
      onSettled: () => {
        queryClient.resetQueries('/job-postings/');
        queryClient.resetQueries('/recruitment-processes/');
      },
      onError: (e, variables, context) => {
        if (
          e.response?.status === 400 &&
          e.response?.data?.message &&
          e.response.data?.errorCode === 'deleteVacancyResponse'
        ) {
          notification.error({
            duration: 0,
            message: 'Erro',
            description: e.response?.data.message,
          });
          return;
        }

        //já deveria ser tratado em cloneMutation, barra a propagação
        if (action === 'CLONE') {
          return;
        }

        return queryClient.defaultMutationOptions().onError?.(e, variables, context);
      },
    }
  );

  const handleSubmit = async () => {
    mutation.mutate({ jobPosting });
  };

  const handleConcludeVacancy = async () => {
    if (jobPosting.filled_positions >= jobPosting.positions) {
      mutation.mutate({ jobPosting });
    } else {
      if (onOk) {
        onOk();
      }
    }
  };

  return (
    <>
      <ConcludeVacancyModal
        title={
          <>
            <Trans>Concluir vaga</Trans> <strong>{jobPosting.name}</strong>
          </>
        }
        cancelButtonProps={{ hidden: jobPosting.filled_positions < jobPosting.positions }}
        jobPosting={jobPosting}
        open={open && action === 'FINALIZED'}
        onOk={handleConcludeVacancy}
        onCancel={onCancel}
        confirmLoading={mutation.isLoading}
        destroyOnClose
      />
      <FreezeVacancyModal
        title={
          <>
            <Trans>Congelar vaga</Trans> <strong>{jobPosting.name}</strong>
          </>
        }
        jobPosting={jobPosting}
        open={open && action === 'SUSPENDED'}
        onOk={handleSubmit}
        onCancel={onCancel}
        confirmLoading={mutation.isLoading}
        form={form}
        destroyOnClose
      />
      <CancelVacancyModal
        title={
          <>
            <Trans>Cancelar vaga</Trans> <strong>{jobPosting.name}</strong>
          </>
        }
        jobPosting={jobPosting}
        open={open && action === 'CANCELED'}
        onOk={handleSubmit}
        onCancel={onCancel}
        confirmLoading={mutation.isLoading}
        form={form}
        destroyOnClose
      />
      <DeleteVacancyModal
        title={
          <>
            <Trans>Deletar vaga</Trans> <strong>{jobPosting.name}</strong>
          </>
        }
        jobPosting={jobPosting}
        open={open && action === 'DELETE'}
        onOk={handleSubmit}
        onCancel={onCancel}
        confirmLoading={mutation.isLoading}
        form={form}
        destroyOnClose
      />
      <CloneVacancyModal
        title={
          <>
            <Trans>Clonar vaga</Trans> <strong>{jobPosting.name}</strong>
          </>
        }
        jobPosting={jobPosting}
        open={open && action === 'CLONE'}
        onOk={handleSubmit}
        okText={t('Sim')}
        onCancel={!cloneJobPostingMutation.isLoading ? onCancel : undefined}
        cancelText={t('Não')}
        confirmLoading={mutation.isLoading}
        form={form}
        closable={!cloneJobPostingMutation.isLoading}
        maskClosable={!cloneJobPostingMutation.isLoading}
        destroyOnClose
      />
    </>
  );
};

export default VacancyActionsModal;
